/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import {
  AbstractFormComponent,
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  EmailAttachment,
  FormActionPipelines,
  FormFlowState,
  PrinterBusinessService,
  ReportService,
  whenFormInvalid,
} from '@gh/builder-shared';
import { AelEvents, createLinkFromAttachment } from '@gh/builder-shared-data';
import { textMessageDialogRoute } from '@gh/common-internal-shared';
import { ActionFlow, FormStateSnapshot, LogicalAction, streamValueAction } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { SlicedDataSource } from '@gh/core-data';
import { isMessageBasket } from '@gh/core-messages';
import { TranslationService } from '@gh/core-mls';
import {
  DialogService,
  NotificationService,
  TextMessageDialogConfig,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { createLink, openInNewWindow, pipeableValue, PipeableValue } from '@gh/core-util';
import { merge$, of$, throwError$ } from '@gh/rx';
import { catchError$, filter$, isEmpty$, map$, mapTo$, share$, switchMap$, tap$ } from '@gh/rx/operators';
import { noop } from 'lodash';
import { Observable } from 'rxjs';
import { WipBusinessService } from '../../resources/wip-business.service';
import { ActivatedRoute } from '@angular/router';
import {
  calculateWipJobTotal,
  wipCreateAndPrintReportData,
  WipCreateAndPrintRequest,
  WipCreateAndPrintResponse,
  WipDetails,
  WipJournalLogItem,
  WipMasterInfo,
  WipTab,
} from '../../resources/wip.model';
import { wipJournalsDialogRoute } from '../wip-journals-dialog/wip-journals-dialog.component';
import { WipFormState } from './wip-form.service';

@Component({
  selector: 'gh-wip',
  template: require('./wip-details.component.html'),
  providers: [WipFormState],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WipDetailsComponent extends AbstractFormComponent<WipDetails> implements OnInit {
  dataSource$: PipeableValue<SlicedDataSource<WipJournalLogItem>>;

  defaultMonthYear: any;

  constructor(private waitings: WaitingService,
              private notifications: NotificationService,
              private translations: TranslationService,
              private dialogs: DialogService,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              private userContext: BuilderUserContext,
              private navigations: BuilderNavigationService,
              private business: WipBusinessService,
              private reports: ReportService,
              private printers: PrinterBusinessService,
              public form: WipFormState,
              private route: ActivatedRoute,
              ) {
    super();

    this.defaultMonthYear = {
      month: this.route.snapshot.paramMap.get('month'),
      year: Number(this.route.snapshot.paramMap.get('year')),
    };
  }

  get completeFlow(): ActionFlow<FormFlowState<WipDetails>> {
    return this.pipelines.complete({
      noSuccessNotification: true,
      validateForm: this.commonActions.validateWithEffect(whenFormInvalid, () => this.handleErrors()),
      execute: this.saveAction,
      finish: ({ value }) => {
        this.reload(value);
      },
    });
  }

  get recalculateFlow(): ActionFlow<FormFlowState<WipDetails>> {
    return this.pipelines.recalculate({
      validateForm: this.commonActions.validateWithEffect(whenFormInvalid, () => this.handleErrors()),
      execute: this.commonActions.execute(({ value }) => this.business.recalculate(value, false)),
      finish: ({ value }: any) => this.form.mergeOnRecalculate(value),
    });
  }

  get saveAction(): LogicalAction<FormFlowState<WipDetails>> {
    return streamValueAction((state) =>
      // recalculate values to get actual values
      this.business.recalculate(state.value, false).pipe(
        this.waitings.operator(),
        // merge calculated values
        tap$((response) => this.form.mergeOnRecalculate(response)),
        // show WIP Journals dialog
        switchMap$(() =>
          this.dialogs.openWithInput(wipJournalsDialogRoute, { details: this.form.value })
            .afterClosed()),
        filter$(Boolean),
        // Post Journals
        switchMap$(() =>
          this.business.recalculate(this.form.value, true).pipe(
            this.waitings.operator())),
        // generate & print report
        switchMap$(() =>
          this.printers.getPrinters().pipe(
            this.waitings.operator(),
            switchMap$(({ isCloud }) => this.business.generateReport(this.form.value).pipe(
              this.waitings.operator(),
              switchMap$((report) => {
                if (isCloud) {
                  return this.tryToPrintWipReport(report);
                } else {
                  return this.showWipReportDialog(report);
                }
              }))),

            // show and ignore all errors, because the main process (post journals) was executed successfully
            // => continue flow
            catchError$((error) => {
              this.notifications.processAll(error);
              return [void 0];
            }))),
        // reload init info
        switchMap$(() => this.business.init().pipe(this.waitings.operator())),
        map$((details) => ({ ...state, value: details }))));
  }

  ngOnInit(): void {

    this.business.init().pipe(
      this.waitings.operator())
      .subscribe(
        (details) => {
          details.masterInfo.value.currentMonth = details.masterInfo.value.months.find(
            (el) => el.month === this.defaultMonthYear.month && el.year === this.defaultMonthYear.year,
          );

          this.form.build(details);
        },
        this.notifications.processAll);

    this.dataSource$ = pipeableValue(this.business.findLogItemsAsDataSource());
  }

  canDeactivate(): Observable<boolean> {
    return this.canCancel();
  }

  onMasterInfoChange(masterInfo: FormStateSnapshot<WipMasterInfo>): void {
    this.form.setMasterInfo(masterInfo);
  }

  onJobSetField(id: ID, value?: number): void {
    this.form.setJobField(id, value);
  }

  onTabSelected(tab: WipTab): void {
    this.form.selectTab(tab);
  }

  onApply(): void {
    if (this.form.isMasterInfoInvalid) {
      this.form.highlightMasterInfo();
      return;
    }
    this.business.findAllJobs(this.form.value).pipe(
      this.waitings.operator())
      .subscribe(
        (response) => this.form.applyJobs(response),
        this.notifications.processAll);
  }

  onCloseEmptyYear(): void {
    this.dialogs.confirmation(yesNoConfirmation('msg_wip_close_year_zero')).pipe(
      filter$(yesAnswer),
      switchMap$(() =>
        this.business.closeEmptyYear({...this.form.submitValue}).pipe(
          switchMap$(() => this.business.init()),
          this.waitings.operator())))
      .subscribe(
        (details) => this.reload(details),
        this.notifications.processAll);
  }

  onResetFocusedCell(): void {
    this.form.resetFocusedCell();
  }

  onRecalculate(): void {
    const flow = this.recalculateFlow;

    flow.run({
      value: this.form.value,
      form: this.form,
    });
  }

  goToAel(): void {
    this.navigations.goToAel({
      entityId: this.userContext.office!.id, // tslint:disable-line:no-non-null-assertion
      entityType: 'OFFICE',
      eventTypeIds: [AelEvents.Office.WipPosted, AelEvents.Office.WipPostedNoJobs],
      label: 'lbl_wip_profit_adj',
    });
  }

  onPrintWip(): void {

    this.printers.getPrinters().pipe(
      this.waitings.operator(),
      switchMap$(({ isCloud }) => this.business.generateReport(this.form.value).pipe(
        this.waitings.operator(),
        switchMap$((report) => {
          if (isCloud) {
            return this.tryToPrintWipReport(report);
          } else {
            return this.showWipReportDialog(report);
          }
        })))).subscribe(noop, this.notifications.processAll);

    // this.business.generateReport(this.form.value).pipe(
    //   this.waitings.operator(),
    //   switchMap$((report) => this.tryToPrintWipReport(report)))
    //   .subscribe(noop, this.notifications.processAll);
  }

  onPrint(): void {
    const jobs = this.form.value.jobs;
    const totals = calculateWipJobTotal(jobs);
    const reportData = wipCreateAndPrintReportData(jobs, totals);

    this.printers.getPrinters().pipe(
      this.waitings.operator(),
      switchMap$(({ isCloud }) => this.business.createAndPrintWipJobsReport(reportData).pipe(
        this.waitings.operator(),
        switchMap$((report) => {
          if (isCloud) {
            return this.tryToPrintReport(report, reportData);
          } else {
            return this.showReportDialog(report);
          }
        })))).subscribe(noop, this.notifications.processAll);

    // this.business.createAndPrintWipJobsReport(reportData).pipe(
    //   this.waitings.operator(),
    //   switchMap$((report) => this.showReportDialog(report)))
    //   .subscribe(noop, this.notifications.processAll);
  }

  tryToPrintReport(report: WipCreateAndPrintResponse, reportData: WipCreateAndPrintRequest): Observable<void> {
    const printer$ = this.printers.choosePrinter({
      finder: { title: 'lbl_select_printer_for_producing' },
    }).pipe(share$()); // we use share() here, because we need two subscriptions here below

    // if we select printer => print
    const print$ = printer$.pipe(
      switchMap$(({ authCode, printer }) =>
        this.business.createAndPrintWipJobsReport({
          authCode: authCode!, // tslint:disable-line:no-non-null-assertion
          printerId: printer!.cloudPrinterId, // tslint:disable-line:no-non-null-assertion
          journal: reportData.journal,
          totals: reportData.totals,
        }).pipe(this.waitings.operator())),
      catchError$((error) => {
        if (isMessageBasket(error) && error.has('msg_no_printer')) {
          this.notifications.processAll(error);
          return this.showReportDialog(report); // tslint:disable-line:no-non-null-assertion
        } else {
          return throwError$(error);
        }
      }));

    // if we press on cancel in "find printer dialog" => show link to report
    const noPrint$ = printer$.pipe(
      // ignore any errors
      catchError$(() => []),
      isEmpty$(),
      filter$(Boolean),
      // tslint:disable-next-line:no-non-null-assertion
      switchMap$(() => this.showReportDialog(report)));

    return merge$(print$, noPrint$).pipe(mapTo$(void 0));
  }

  onCancel(): void {
    this.canCancel().pipe(
      filter$(Boolean))
      .subscribe(() => this.form.clear());
  }

  openInXero(item: WipJournalLogItem): void {
    openInNewWindow(item.xeroUrl);
  }

  private handleErrors(): void {
    if (this.form.isMasterInfoInvalid) {
      this.form.highlightMasterInfo();
    } else if (this.form.hasInvalidJob) {
      this.form.focusInvalidJob();
      this.notifications.error('msg_he_incorrect_data_in_tables');
    }
  }

  private tryToPrintWipReport(report: EmailAttachment): Observable<void> {
    const printer$ = this.printers.choosePrinter({
      finder: { title: 'lbl_select_printer_for_producing' },
    }).pipe(share$()); // we use share() here, because we need two subscriptions herebelow

    // if we select printer => print
    const print$ = printer$.pipe(
      switchMap$(({ authCode, printer }) =>
        this.business.print({
          authCode: authCode!, // tslint:disable-line:no-non-null-assertion
          printerId: printer!.cloudPrinterId, // tslint:disable-line:no-non-null-assertion
          report,
        }).pipe(this.waitings.operator())),
      // if printer does not exist => show link to report
      catchError$((error) => {
        if (isMessageBasket(error) && error.has('msg_no_printer')) {
          this.notifications.processAll(error);
          return this.showWipReportDialog(report); // tslint:disable-line:no-non-null-assertion
        } else {
          return throwError$(error);
        }
      }));

    // if we press on cancel in "find printer dialog" => show link to report
    const noPrint$ = printer$.pipe(
      // ignore any errors
      catchError$(() => []),
      isEmpty$(),
      filter$(Boolean),
      // tslint:disable-next-line:no-non-null-assertion
      switchMap$(() => this.showWipReportDialog(report)));

    return merge$(print$, noPrint$).pipe(mapTo$(void 0));
  }

  private showReportDialog(attachment: WipCreateAndPrintResponse): Observable<void> {
    return this.dialogs.openWithInput(textMessageDialogRoute, <TextMessageDialogConfig<string>>{
      title: this.translations.get('lbl_link_to_print_wip'),
      html: [
        this.translations.get('lbl_follow_link_to_print_wip'),
        createLink(attachment.webViewLink, attachment.fileName),
      ].join(' '),
      buttons: [{ value: 'ok', title: this.translations.get('lbl_ok'), type: 'primary' }],
    }).afterClosed();
  }

  private showWipReportDialog(attachment: EmailAttachment): Observable<void> {
    return this.dialogs.openWithInput(textMessageDialogRoute, <TextMessageDialogConfig<string>>{
      title: this.translations.get('lbl_report_work_in_progress_adjustment'),
      html: [
        this.translations.get('lbl_report_follow_work_in_progress_adj'),
        createLinkFromAttachment(attachment),
      ].join(' '),
      buttons: [{ value: 'ok', title: this.translations.get('lbl_ok'), type: 'primary' }],
    }).afterClosed();
  }

  private canCancel(): Observable<boolean> {
    if (this.form.dirty) {
      return this.dialogs.confirmation(yesNoConfirmation('msg_changes_will_be_lost')).pipe(map$(yesAnswer));
    } else {
      return of$(true);
    }
  }

  private reload(details: WipDetails): void {
    this.form.build(details);
    this.dataSource$.set(this.business.findLogItemsAsDataSource());
  }
}
